const GastrointestinalTractNotes = `
<div class="space-y-8 animate-fadeIn">
    
    <!-- 1. Chapter Header -->
    <div>
        <h2 class="text-2xl font-black text-blue-900 mb-3 border-b-2 border-slate-200 pb-2">Pharmacology of Drugs Acting on GIT</h2>
        <p class="text-slate-700 leading-relaxed text-[14px]">
            This section covers drugs used for managing peptic ulcers, altering bowel motility (constipation/diarrhoea), managing emesis (vomiting), and aiding digestion.
        </p>
    </div>

    <!-- 2. Antiulcer Agents -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-blue-800 mb-3 flex items-center gap-2">1. Antiulcer Agents</h3>
        <p class="text-[13px] text-slate-700 mb-3">Peptic ulcers are caused by an imbalance between aggressive factors (Gastric acid, Pepsin, <em>H. pylori</em>, NSAIDs) and defensive factors (Mucus, Bicarbonate, Prostaglandins).</p>
        
        <div class="overflow-x-auto rounded-lg border border-slate-200">
            <table class="min-w-full bg-white text-[13px] text-left">
                <thead class="bg-slate-100 text-slate-700 border-b border-slate-200">
                    <tr><th class="px-4 py-2 font-bold">Class</th><th class="px-4 py-2 font-bold">Drugs</th><th class="px-4 py-2 font-bold">Mechanism of Action (MOA)</th></tr>
                </thead>
                <tbody class="divide-y divide-slate-100 text-slate-600">
                    <tr>
                        <td class="px-4 py-3 font-bold text-blue-900">Proton Pump Inhibitors (PPIs)</td>
                        <td class="px-4 py-3">Omeprazole, Pantoprazole, Rabeprazole</td>
                        <td class="px-4 py-3">Irreversibly inhibit the <strong>H+/K+ ATPase pump</strong> on parietal cells. Most potent acid suppressors. Prodrugs activated in acidic pH.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-blue-900">H2-Receptor Antagonists</td>
                        <td class="px-4 py-3">Ranitidine, Famotidine, Cimetidine</td>
                        <td class="px-4 py-3">Competitively block Histamine H2 receptors on parietal cells, reducing basal and food-stimulated acid secretion.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-blue-900">Antacids</td>
                        <td class="px-4 py-3">Aluminium hydroxide, Magnesium hydroxide</td>
                        <td class="px-4 py-3">Weak bases that directly neutralize secreted gastric acid (forming salt and water). Provide rapid but transient relief.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-blue-900">Ulcer Protectives</td>
                        <td class="px-4 py-3">Sucralfate, Bismuth subcitrate</td>
                        <td class="px-4 py-3">Sucralfate polymerizes in acidic pH to form a sticky gel that physically coats the ulcer crater, preventing further acid attack.</td>
                    </tr>
                    <tr>
                        <td class="px-4 py-3 font-bold text-blue-900">Anti-H. pylori Drugs</td>
                        <td class="px-4 py-3">Amoxicillin, Clarithromycin, Metronidazole</td>
                        <td class="px-4 py-3">Antibiotics used in a "Triple Therapy" regimen (PPI + 2 Antibiotics) to eradicate <em>Helicobacter pylori</em> infection.</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <!-- 3. Constipation and Diarrhoea -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div class="bg-blue-50 border-t-4 border-blue-600 p-6 rounded-b-xl shadow-sm">
            <h3 class="text-[16px] font-black text-blue-800 mb-2">2A. Drugs for Constipation (Laxatives/Purgatives)</h3>
            <ul class="list-disc pl-5 space-y-2 text-[13px] text-slate-700">
                <li><strong>Bulk-forming:</strong> Psyllium (Ispaghula), Bran. Absorb water, swell up, and increase fecal bulk, stimulating peristalsis.</li>
                <li><strong>Osmotic Laxatives:</strong> Lactulose, Milk of Magnesia. Retain water in the colon via osmotic pressure.</li>
                <li><strong>Stimulant Purgatives:</strong> Bisacodyl, Senna. Irritate the intestinal mucosa to stimulate vigorous peristalsis.</li>
                <li><strong>Stool Softeners:</strong> Docusate. Act as surfactants, allowing water to penetrate feces.</li>
            </ul>
        </div>

        <div class="bg-red-50 border-t-4 border-red-500 p-6 rounded-b-xl shadow-sm">
            <h3 class="text-[16px] font-black text-red-800 mb-2">2B. Drugs for Diarrhoea</h3>
            <ul class="list-disc pl-5 space-y-2 text-[13px] text-slate-700">
                <li><strong>Oral Rehydration Salts (ORS):</strong> Primary therapy to prevent severe dehydration and electrolyte loss.</li>
                <li><strong>Antimotility Agents:</strong> Loperamide, Diphenoxylate. (MOA: Agonists at peripheral &mu;-opioid receptors in the gut. They decrease peristalsis and increase transit time).</li>
                <li><strong>Antisecretory Agents:</strong> Racecadotril (Enkephalinase inhibitor, prevents hypersecretion).</li>
            </ul>
        </div>
    </div>

    <!-- 4. Emetics and Anti-Emetics -->
    <div class="bg-white p-6 border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <h3 class="text-[18px] font-black text-blue-800 mb-3">3. Emetics and Anti-emetics</h3>
        
        <div class="mb-4">
            <h4 class="text-[15px] font-bold text-blue-700 mb-1">Anti-emetics (Prevent Vomiting)</h4>
            <p class="text-[13px] text-slate-700 mb-2">Vomiting is controlled by the Chemoreceptor Trigger Zone (CTZ) and the vomiting center in the medulla.</p>
            <ul class="list-disc pl-5 space-y-2 text-[13px] text-slate-600">
                <li><strong>5-HT3 Antagonists (Ondansetron, Granisetron):</strong> Drug of choice for Chemotherapy-Induced Nausea and Vomiting (CINV). Block 5-HT3 receptors in the gut and CTZ.</li>
                <li><strong>D2-Receptor Antagonists (Metoclopramide, Domperidone):</strong> Block dopamine D2 receptors in CTZ. Metoclopramide is also a <em>Prokinetic</em> (increases gastric emptying).</li>
                <li><strong>Anticholinergics/Antihistaminics (Hyoscine, Promethazine):</strong> Drug of choice for <em>Motion Sickness</em>.</li>
                <li><strong>NK1 Receptor Antagonists (Aprepitant):</strong> Blocks Substance P in the brain. Used for delayed CINV.</li>
            </ul>
        </div>

        <div class="bg-slate-50 p-3 rounded border border-slate-100">
            <h4 class="text-[14px] font-bold text-slate-800 mb-1">Emetics (Induce Vomiting)</h4>
            <p class="text-[13px] text-slate-600">Used rarely, mostly in poisonings (if the patient is conscious and the poison is non-corrosive).<br>
            <strong>Examples:</strong> Ipecacuanha (syrup of Ipecac), Apomorphine.</p>
        </div>
    </div>

    <!-- 5. Appetite Stimulants & Digestants -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div class="bg-white p-5 border border-slate-200 rounded-xl shadow-sm">
            <h3 class="text-[15px] font-black text-blue-800 mb-2">4. Appetite Stimulants & Suppressants</h3>
            <ul class="list-disc pl-5 space-y-1 text-[13px] text-slate-600">
                <li><strong>Appetite Stimulants (Orexigenics):</strong> <em>Cyproheptadine</em> (an antihistamine with anti-serotonergic activity that stimulates appetite).</li>
                <li><strong>Appetite Suppressants (Anorectics):</strong> <em>Sibutramine</em> (withdrawn), <em>Phentermine</em>. Primarily act by increasing noradrenaline/serotonin in the brain. Used for short-term obesity management.</li>
            </ul>
        </div>

        <div class="bg-white p-5 border border-slate-200 rounded-xl shadow-sm">
            <h3 class="text-[15px] font-black text-blue-800 mb-2">5. Digestants and Carminatives</h3>
            <ul class="list-disc pl-5 space-y-1 text-[13px] text-slate-600">
                <li><strong>Digestants:</strong> Substances intended to promote digestion of food. Examples include <em>Pepsin, Pancreatin, Diastase</em>. Given when endogenous enzyme secretion is deficient (e.g., chronic pancreatitis).</li>
                <li><strong>Carminatives:</strong> Agents that prevent the formation of gas in the GI tract or facilitate its expulsion. Examples include <em>Simethicone</em> (reduces surface tension of gas bubbles), peppermint oil, fennel.</li>
            </ul>
        </div>
    </div>

</div>
`;

export default GastrointestinalTractNotes;